import React from 'react';

export type FilterSelectionMode = 'single' | 'multi';

export interface FilterButtonOption<T extends string = string> {
    key: T;
    label: string;
    style?: React.CSSProperties;
    className?: string;
    disabled?: boolean;
}

interface FilterButtonGroupProps<T extends string = string> {
    options: FilterButtonOption<T>[];
    selectedMap: Record<T, boolean>;
    onSelectionChange: (nextSelection: Record<T, boolean>) => void;
    mode?: FilterSelectionMode;
    allowEmptySingleSelect?: boolean;
    containerClassName?: string;
    containerStyle?: React.CSSProperties;
    buttonClassName?: string;
    getButtonStyle?: (isActive: boolean, option: FilterButtonOption<T>) => React.CSSProperties;
}

const FilterButtonGroup = <T extends string = string>({
    options,
    selectedMap,
    onSelectionChange,
    mode = 'multi',
    allowEmptySingleSelect = false,
    containerClassName = '',
    containerStyle,
    buttonClassName = 'btn',
    getButtonStyle
}: FilterButtonGroupProps<T>) => {
    const handleOptionClick = (clickedKey: T) => {
        if (mode === 'single') {
            const isCurrentlyActive = Boolean(selectedMap[clickedKey]);
            const shouldActivate = allowEmptySingleSelect ? !isCurrentlyActive : true;
            const nextSelection = {} as Record<T, boolean>;

            options.forEach((option) => {
                nextSelection[option.key] = shouldActivate && option.key === clickedKey;
            });

            onSelectionChange(nextSelection);
            return;
        }

        onSelectionChange({
            ...selectedMap,
            [clickedKey]: !selectedMap[clickedKey]
        });
    };

    return (
        <div className={containerClassName} style={containerStyle}>
            {options.map((option) => {
                const isActive = Boolean(selectedMap[option.key]);
                const activeStyle = getButtonStyle ? getButtonStyle(isActive, option) : {};

                return (
                    <button
                        key={option.key}
                        type="button"
                        className={`${buttonClassName} ${option.className || ''}`.trim()}
                        style={{ ...activeStyle, ...option.style }}
                        onClick={() => handleOptionClick(option.key)}
                        disabled={option.disabled}
                    >
                        {option.label}
                    </button>
                );
            })}
        </div>
    );
};

export default FilterButtonGroup;
